/**
 * Canonical Single-Select Account Dropdown
 *
 * Consolidates the ImportIgPostsModal/AccountSelector and media-library/account-selector
 * implementations into a single, reusable component.
 *
 * Features:
 * - Popover + Command pattern (shadcn)
 * - Platform filtering via props
 * - Optional dynamic width matching
 * - Search functionality
 * - Account avatars with status indicators
 * - Controlled component API
 */

import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  dropdownChevronClassName,
  dropdownTriggerClassName,
} from "@/components/ui/dropdown-trigger-styles";
import { cn } from "@/lib/utils";
import { useTriggerWidth } from "@/components/ui/use-trigger-width";
import AccountAvatar from "components/AccountAvatar/AccountAvatar";
import { getPlatformNameType } from "utils/accounts";
import { AccountType } from "shared/types/accounts";
import { AccountOption } from "./components/account-option";
import { useAccountFiltering } from "./hooks";
import { SingleSelectAccountDropdownProps } from "./types";
import { getAccountLabel } from "./account-label";

export function SingleSelectAccountDropdown({
  accounts,
  value,
  onChange,
  platforms,
  placeholder = "Select account...",
  className,
  disabled = false,
  scopeFilter,
}: SingleSelectAccountDropdownProps) {
  const [open, setOpen] = useState(false);
  const { triggerRef, triggerWidthStyle } =
    useTriggerWidth<HTMLButtonElement>(open);

  // Filter accounts by platform
  const filteredByPlatform = useAccountFiltering(accounts, platforms);

  // Apply optional custom scope filter
  const filteredAccounts = useMemo(() => {
    if (!scopeFilter) return filteredByPlatform;
    return filteredByPlatform.filter(scopeFilter);
  }, [filteredByPlatform, scopeFilter]);

  // Group accounts by brandName
  const groupedAccounts = useMemo(() => {
    const groups: Record<string, AccountType[]> = {};

    filteredAccounts.forEach((account) => {
      const groupKey = account.brandName || "Other Accounts";
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(account);
    });

    return groups;
  }, [filteredAccounts]);

  const onAccountSelect = (account: AccountType) => {
    onChange(account);
    setOpen(false);
  };

  const selectedAccountName = useMemo(() => {
    if (!value) return placeholder;
    return getAccountLabel(value);
  }, [value, placeholder]);

  // Determine empty message based on platform filter
  const emptyMessage = useMemo(() => {
    if (platforms && platforms.length === 1 && platforms[0] === "IG") {
      return "No Instagram accounts found.";
    }
    return "No accounts found.";
  }, [platforms]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "h-10 w-full justify-between",
            "font-inter text-sm font-medium",
            dropdownTriggerClassName,
            className
          )}
        >
          {value ? (
            <div className="flex items-center w-full min-w-0">
              <AccountAvatar
                profilePictureUrl={value.profilePictureUrl}
                platformType={getPlatformNameType(value).platformType}
                color={value.color}
                size={"small"}
                placement={"top"}
                className="mr-2 flex-shrink-0"
                useYtRedPlayIcon
              />
              <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                {selectedAccountName}
              </span>
            </div>
          ) : (
            <span className="text-neutral-500">{selectedAccountName}</span>
          )}
          <i
            className={cn(
              "fa-regular fa-chevron-down ml-2 h-4 w-4 shrink-0",
              dropdownChevronClassName
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 border-neutral-100"
        disableAnimation
        style={triggerWidthStyle}
        sideOffset={4}
        align="start"
      >
        <Command>
          <CommandInput
            placeholder="Search accounts..."
            className="h-9 border-neutral-100 font-inter text-sm"
          />
          <CommandEmpty className="py-6 text-center font-inter text-sm text-neutral-500">
            {emptyMessage}
          </CommandEmpty>
          <CommandList>
            {Object.entries(groupedAccounts).map(([groupName, accounts]) => (
              <CommandGroup
                key={groupName}
                heading={groupName}
                className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-neutral-700"
              >
                {accounts.map((account) => {
                  const searchValue = [getAccountLabel(account)]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <CommandItem
                      key={account._id}
                      value={`${searchValue}-${account._id}`}
                      onSelect={() => onAccountSelect(account)}
                      className="font-inter font-normal text-sm text-neutral-600 cursor-pointer data-[selected=true]:text-neutral-900"
                    >
                      <AccountOption account={account} />
                      {value?._id === account._id && (
                        <i className="fa-solid fa-check ml-auto h-4 w-4 text-blue-500" />
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
